import { styled } from '@mui/material'
import type { ReactNode } from 'react'

type TopBarProps = {
  /** Before the title: the back or close button, and Chat when the page offers it. */
  leading?: ReactNode
  /** Breadcrumbs, or the page's title with its status. The bar sets the type style; plain text truncates on its own,
   * and text sharing a row with other content goes in a one-row `ClampedTypography` so it can. */
  title?: ReactNode
  /** `center` for the focus pages with a single centred title. */
  titleAlign?: 'start' | 'center'
  /** `h1` when the title is the page's heading: on focus pages, which have no page header below the bar. */
  titleAs?: 'div' | 'h1'
  /** The page's own controls, such as its actions or who else is viewing. Always the page's, never the shell's.
   * Each child sits a gap apart, so group a tight cluster, such as a set of icon buttons, before passing it. */
  actions?: ReactNode
  /** Last on the right, after `actions`: the controls the shell keeps on every page, such as Help and the
   * account. Always the shell's. */
  trailing?: ReactNode
  /** Striped bar for QA organisations. */
  qa?: boolean
  /** Phone width: the title starts at the left. The shell sets this, never a page. */
  compact?: boolean
}

type OwnerState = { qa: boolean; centred: boolean }

const shouldForwardProp = (prop: PropertyKey) => prop !== 'ownerState'

const Root = styled('header', { shouldForwardProp })<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  display: ownerState.centred ? 'grid' : 'flex',
  // Equal side columns centre the title on the bar. Neither shrinks below its own controls, so when space runs
  // short the title first moves off centre, then truncates.
  gridTemplateColumns: ownerState.centred ? 'minmax(max-content, 1fr) minmax(0, auto) minmax(max-content, 1fr)' : undefined,
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(5),
  flexShrink: 0,
  height: '3.5rem',
  boxSizing: 'border-box',
  padding: theme.spacing(2.5, 5),
  backgroundColor: theme.palette.bg.surface.default,
  backgroundImage: ownerState.qa
    ? `repeating-linear-gradient(-45deg, ${theme.palette.bg.neutral.muted} 0 1.125rem, transparent 1.125rem 2.25rem)`
    : undefined,
  borderBottom: `${theme.strokeWidth.default} solid ${theme.palette.border.subtle}`,
}))

const Start = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(5),
  flex: '1 1 auto',
  minWidth: 0,
}))

// A block rather than a flex row, so text placed straight in it can end in an ellipsis.
const Title = styled('div')(({ theme }) => ({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  // As an `h1` it stays the bar's title, not the browser's heading style.
  margin: 0,
  ...theme.typography['body-medium'],
  color: theme.palette.text.default,
}))

const End = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: theme.spacing(4),
  flexShrink: 0,
  // Without a title, auto-placement would put it in a centred bar's middle column.
  gridColumn: 3,
}))

/**
 * The shell's top bar. Pages fill its slots and the bar does the layout: one height, padding and set of gaps for
 * every page, with any slot left empty taking no room.
 */
export const TopBar = ({
  leading,
  title,
  titleAlign = 'start',
  titleAs = 'div',
  actions,
  trailing,
  qa = false,
  compact = false,
}: TopBarProps) => {
  const centred = titleAlign === 'center' && !compact
  const titleSlot = title != null && title !== false && title !== '' && <Title as={titleAs}>{title}</Title>

  const end = (
    <End>
      {actions}
      {trailing}
    </End>
  )

  return (
    <Root ownerState={{ qa, centred }}>
      {centred ? (
        <>
          <Start>{leading}</Start>
          {titleSlot}
          {end}
        </>
      ) : (
        <>
          <Start>
            {leading}
            {titleSlot}
          </Start>
          {end}
        </>
      )}
    </Root>
  )
}
